import { captureFirstTouch, getStoredAttribution, OWN_HOSTS } from "@/lib/attribution";
import { agregarReferenciaWhatsapp } from "@/lib/attribution-whatsapp";
import { lazy, Suspense, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import appCssPublic from "../styles-public.css?url";
import { WhatsAppFab } from "@/components/site/WhatsAppFab";
import { SITE, SITE_URL, SERVICES } from "@/lib/site";
import { DEFAULT_CONTACT, type ContactInfo } from "@/lib/site-contact";
import { getPublicContactInfo } from "@/server/site/contact.fns";
import { ContactInfoProvider } from "@/components/site/ContactInfoProvider";
import { DEFAULT_MARKETING_CONFIG, type MarketingConfig } from "@/lib/site-marketing";
import { getPublicMarketingConfig } from "@/server/site/marketing.fns";
import { MarketingConfigProvider } from "@/components/site/MarketingConfigProvider";
import { getSeoOverrideForPath, type SeoPageOverride } from "@/server/site/seo-override.fns";
import { getSiteImageSlots } from "@/server/site/site-image-slots.fns";
import { getPageContentForPath } from "@/server/site/page-content.fns";
import type { PageContentResolved } from "@/lib/page-content/tipos";
import type { SiteImageSlotsResolved } from "@/lib/site-image-slots";
import { BUSINESS_HOURS } from "@/lib/whatsapp-message";

const SCHEMA_DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const GA_ID = "G-LK75PQG3TL";

function esRutaAdmin(pathname: string): boolean {
  return pathname === "/crm" || pathname.startsWith("/crm/") || pathname.startsWith("/admin");
}

// sonner (~34 KB de JS) solo se necesita cuando algo dispara un toast — en el
// sitio público eso pasa únicamente al enviar LeadForm/PostularForm, que son
// chunks lazy que el usuario alcanza recién tras interactuar. Cargar el
// Toaster diferido al idle del navegador (mismo patrón que gtag.js abajo) lo
// saca del JS inicial de todas las páginas sin perder toasts en la práctica:
// para cuando un formulario puede llegar a llamar toast(), el Toaster lleva
// segundos montado.
const LazyToaster = lazy(() => import("sonner").then((m) => ({ default: m.Toaster })));

function DeferredToaster() {
  const [mount, setMount] = useState(false);

  useEffect(() => {
    const w = window as typeof window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(() => setMount(true), { timeout: 3000 });
    } else {
      const t = setTimeout(() => setMount(true), 2000);
      return () => clearTimeout(t);
    }
  }, []);

  if (!mount) return null;
  return (
    <Suspense fallback={null}>
      <LazyToaster position="top-center" richColors closeButton />
    </Suspense>
  );
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página no encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          La página que buscas no existe o cambió de dirección.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página no cargó correctamente
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Ocurrió un error inesperado. Puedes intentar de nuevo o volver al inicio.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Intentar de nuevo
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Volver al inicio
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  seoOverride: SeoPageOverride | null;
  siteImageSlots: SiteImageSlotsResolved | null;
  /** Texto efectivo de la página (editado o por defecto); {} si la ruta no es editable (specs/138). */
  pageContent: PageContentResolved;
  contact: ContactInfo;
  marketing: MarketingConfig;
}>()({
  beforeLoad: async ({ location }) => {
    // Las 4 llamadas son independientes entre sí (cada una con su propio
    // caché en memoria, ninguna depende del resultado de otra) pero antes
    // vivían en dos etapas separadas (beforeLoad, luego loader) que TanStack
    // Router resuelve en secuencia — dos round-trips en vez de uno solo,
    // inflando el TTFB de cada visita. Todo junto acá, en un solo
    // Promise.all: seoOverride/siteImageSlots quedan en el contexto (se
    // necesita para propagarse a `head()` de las rutas hijas — loaderData
    // del loader raíz no llega ahí, context sí, ver
    // specs/111-slots-imagenes-sitio research.md Decisión 4); contact/
    // marketing también, y el loader de abajo solo los copia sin red extra.
    const [seoOverride, siteImageSlots, pageContent, contact, marketing] = await Promise.all([
      getSeoOverrideForPath({ data: { path: location.pathname } }),
      getSiteImageSlots(),
      // Texto editable de la página (specs/138): degrada a {} y cada componente usa su texto por defecto.
      getPageContentForPath({ data: { path: location.pathname } }).catch(
        (): PageContentResolved => ({}),
      ),
      getPublicContactInfo(),
      getPublicMarketingConfig(),
    ]);
    return { seoOverride, siteImageSlots, pageContent, contact, marketing };
  },
  loader: ({ context }): { contact: ContactInfo; marketing: MarketingConfig } => ({
    contact: context.contact,
    marketing: context.marketing,
  }),
  head: ({ loaderData, matches }) => {
    const contact = loaderData?.contact ?? DEFAULT_CONTACT;
    const marketing = loaderData?.marketing ?? DEFAULT_MARKETING_CONFIG;
    const pathname = matches[matches.length - 1]?.pathname ?? "";
    const isAdminRoute = esRutaAdmin(pathname);

    return {
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: "Improve Yourself · Agencia de activaciones BTL en Lima" },
        {
          name: "description",
          content:
            "Agencia BTL en Lima. Activaciones, eventos corporativos, impulsadoras, sampling, trade marketing y volanteo.",
        },
        { name: "author", content: "Improve Yourself" },
        { property: "og:site_name", content: "Improve Yourself" },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "es_PE" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow" },
        { name: "format-detection", content: "telephone=no" },
        { name: "theme-color", content: "#120907" },
      ],
      scripts: [
        ...(!isAdminRoute
          ? [
              // El stub de dataLayer/gtag se define de inmediato (síncrono, sin red)
              // para que cualquier gtag(...) de la app quede encolado desde el primer
              // render. El script real de gtag.js (~330 KB entre GA4 + Ads, ver
              // RootComponent) se carga diferido tras quedar el navegador idle, para
              // no competir por el hilo principal con el LCP — mismo patrón que
              // HeroBackground.
              {
                children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GA_ID}',{'send_page_view':false});`,
              },
              // Cargado sitio-wide (no solo en /cotizar/gracias) para que Google Ads
              // pueda capturar el gclid en la página donde aterriza el clic del anuncio.
              ...(marketing.googleAdsConversionId
                ? [{ children: `gtag('config','${marketing.googleAdsConversionId}');` }]
                : []),
            ]
          : []),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${SITE_URL}/#organization`,
            name: SITE.name,
            alternateName: `${SITE.name} — Agencia BTL`,
            legalName: SITE.legalName,
            taxID: SITE.ruc,
            description: SITE.description,
            foundingDate: SITE.foundingYear,
            email: contact.email,
            telephone: contact.phone,
            priceRange: "$$",
            image: `${SITE_URL}/og-default.png`,
            logo: `${SITE_URL}/apple-touch-icon.png`,
            // Misma cobertura que el pie de página y llms.txt (SITE.coverage): todo el
            // Perú, con base en Lima.
            areaServed: [
              { "@type": "Country", name: "Perú" },
              { "@type": "City", name: "Lima" },
              { "@type": "AdministrativeArea", name: "Callao" },
            ],
            sameAs: [SITE.googleBusinessUrl],
            address: {
              "@type": "PostalAddress",
              addressLocality: "Lima",
              addressCountry: "PE",
            },
            openingHoursSpecification: {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: BUSINESS_HOURS.days.map((d) => SCHEMA_DAY_NAMES[d]),
              opens: `${String(BUSINESS_HOURS.startHour).padStart(2, "0")}:00`,
              closes: `${String(BUSINESS_HOURS.endHour).padStart(2, "0")}:00`,
            },
            url: SITE_URL,
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Servicios BTL",
              itemListElement: SERVICES.map((s) => ({
                "@type": "Offer",
                itemOffered: {
                  "@type": "Service",
                  name: s.name,
                  url: `${SITE_URL}${s.slug}`,
                },
              })),
            },
          }),
        },
      ],
      links: [
        // Carga bloqueante normal: cargarlo vía preload+swap (patrón probado
        // en un merge anterior) saca el CSS de la ruta crítica de renderizado
        // pero pinta el HTML sin estilos hasta que el swap ocurre — como este
        // proyecto concentra TODO el estilado (Tailwind) en este único
        // archivo, esa ventana sin CSS causaba un cambio de layout severo
        // (CLS ~1.0, ej. el logo del header se veía gigante sin su clase
        // h-10 hasta que el CSS aplicaba). Se revierte a bloqueante: pintar
        // un poco más tarde pero ya con el layout final es mejor que pintar
        // rápido y reacomodar todo después. (Se probó también `?inline` para
        // embeber el CSS en el HTML y ahorrar el round-trip, pero el pipeline
        // de rolldown-vite 8.1 + tanstack-start falla al compilarlo —
        // TypeError en renderAssetUrlInJS. Reintentar cuando suba Vite.)
        { rel: "stylesheet", href: appCssPublic },
        // Solo cuando Google Tag Gateway está apagado: si está activo, gtag.js
        // y las llamadas de medición se sirven desde el propio dominio (vía
        // Cloudflare), así que un preconnect a los dominios de Google no
        // ahorra nada y solo compite por conexiones tempranas con recursos
        // que sí importan.
        ...(!isAdminRoute && !marketing.googleTagGatewayEnabled
          ? [
              { rel: "preconnect", href: "https://www.googletagmanager.com" },
              { rel: "dns-prefetch", href: "https://www.google-analytics.com" },
            ]
          : []),
        {
          rel: "preload",
          as: "font",
          type: "font/woff2",
          href: "/fonts/inter-latin.woff2",
          crossOrigin: "anonymous",
        },
        {
          // Playfair Display italic 400/latin: usada por el acento del <h1>
          // del Hero ("y eventos corporativos."), above-the-fold, y en casi
          // todas las demás secciones de la home (Coverage, FAQ, FinalCTA,
          // Process, Sectors, Services, BlogPreview) — sin preload queda
          // descubierta recién cuando el CSS bloqueante termina de parsear.
          rel: "preload",
          as: "font",
          type: "font/woff2",
          href: "/fonts/playfair-display-italic-latin.woff2",
          crossOrigin: "anonymous",
        },
        { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
        { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
        { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
        { rel: "manifest", href: "/site.webmanifest" },
        { rel: "sitemap", type: "application/xml", href: "/sitemap.xml" },
      ],
    };
  },
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="es-PE" className="scroll-smooth">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

declare global {
  function gtag(...args: unknown[]): void;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { contact, marketing } = Route.useLoaderData();
  const router = useRouter();

  // Carga real de gtag.js diferida al idle del navegador: el stub de
  // dataLayer ya definido en <head> encola cualquier gtag(...) llamado antes
  // de que este script llegue, así que retrasarlo no pierde eventos — solo
  // evita que ~330 KB de JS de terceros (GA4 + Google Ads) compitan por el
  // hilo principal justo cuando se está pintando el LCP.
  // Primer contacto del visitante (página de aterrizaje, referrer, UTM, gclid)
  // para atribuir los leads a un canal/página (src/lib/attribution.ts).
  useEffect(() => {
    if (esRutaAdmin(window.location.pathname)) return;
    captureFirstTouch(OWN_HOSTS);
  }, []);

  // Los leads de WhatsApp no traen página ni canal: al hacer clic en cualquier enlace a wa.me se
  // agrega al texto prellenado una referencia corta que el CRM lee al crear el lead
  // (src/lib/attribution-whatsapp.ts). En captura, antes de que el navegador siga el enlace.
  useEffect(() => {
    if (esRutaAdmin(window.location.pathname)) return;
    const alActivar = (e: MouseEvent) => {
      const enlace = (e.target as Element | null)?.closest?.("a[href]");
      if (!(enlace instanceof HTMLAnchorElement)) return;
      if (!/^https:\/\/(wa\.me|api\.whatsapp\.com)\//.test(enlace.href)) return;
      enlace.href = agregarReferenciaWhatsapp(enlace.href, getStoredAttribution(OWN_HOSTS));
    };
    document.addEventListener("click", alActivar, true);
    document.addEventListener("auxclick", alActivar, true);
    return () => {
      document.removeEventListener("click", alActivar, true);
      document.removeEventListener("auxclick", alActivar, true);
    };
  }, []);

  useEffect(() => {
    if (esRutaAdmin(window.location.pathname)) return;

    const loadGtag = () => {
      if (document.querySelector(`script[src*="gtag/js?id=${GA_ID}"]`)) return;
      const script = document.createElement("script");
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
      script.async = true;
      document.head.appendChild(script);
    };

    const schedule = (cb: () => void) => {
      const w = window as typeof window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      };
      if (typeof w.requestIdleCallback === "function") {
        w.requestIdleCallback(cb, { timeout: 3000 });
      } else {
        setTimeout(cb, 2000);
      }
    };

    schedule(loadGtag);
  }, []);

  useEffect(() => {
    const sendPageView = () => {
      const isLocal = ["localhost", "127.0.0.1"].includes(window.location.hostname);
      if (typeof gtag === "function" && !isLocal && !esRutaAdmin(window.location.pathname)) {
        gtag("event", "page_view", {
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    };

    sendPageView();
    return router.subscribe("onResolved", sendPageView);
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <ContactInfoProvider value={contact}>
        <MarketingConfigProvider value={marketing}>
          <Outlet />
          <WhatsAppFab />
          <DeferredToaster />
        </MarketingConfigProvider>
      </ContactInfoProvider>
    </QueryClientProvider>
  );
}
